import { useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, newRef, today, useLoad, useSubmit } from "@/lib/api";
import { ErrorBox, Field, Modal } from "@/components/common";
import PatientPicker from "@/components/PatientPicker";

export const CASE_STATUSES = [["sent", "Sent to lab"], ["in_progress", "In progress"], ["received", "Received"], ["fitted", "Fitted"], ["remake", "Remake"], ["cancelled", "Cancelled"]].map(([value, label]) => ({ value, label }));
const METHODS = ["Cash", "UPI", "Bank transfer", "Card"];

const LabNames = ({ labs }) => <datalist id="lab-names">{labs.map((l) => <option key={l.lab_name} value={l.lab_name} />)}</datalist>;

export function LabCaseForm({ patientId, labs = [], onClose, onSaved }) {
  const [form, setForm] = useState({ patient_id: patientId || "", lab_name: "", work_type: "", tooth: "", shade: "", sent_date: today(), due_date: "", charge: "", status: "sent", notes: "" });
  const { busy, error, run } = useSubmit();
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const save = () => run(async () => {
    const r = await api.post("/lab/cases", { ...form, charge: Number(form.charge), due_date: form.due_date || null });
    toast.success(`Lab case sent to ${r.data.lab_name}`); onSaved(r.data);
  });
  return (
    <Modal eyebrow="LABORATORY" title="New lab case" onClose={onClose} onSubmit={save} testid="lab-case-form">
      {!patientId && <PatientPicker value={form.patient_id} onChange={set("patient_id")} testid="lab-case-patient-select" />}
      <div className="form-two">
        <Field label="Lab name" required list="lab-names" testid="lab-case-lab-input" value={form.lab_name} onChange={set("lab_name")} />
        <Field label="Work type" required testid="lab-case-work-input" value={form.work_type} onChange={set("work_type")} placeholder="e.g. PFM crown" />
      </div>
      <LabNames labs={labs} />
      <div className="form-two">
        <Field label="Tooth" testid="lab-case-tooth-input" value={form.tooth} onChange={set("tooth")} />
        <Field label="Shade" testid="lab-case-shade-input" value={form.shade} onChange={set("shade")} />
      </div>
      <div className="form-two">
        <Field label="Sent on" type="date" required testid="lab-case-sent-input" value={form.sent_date} onChange={set("sent_date")} />
        <Field label="Due back" type="date" testid="lab-case-due-input" value={form.due_date} onChange={set("due_date")} />
      </div>
      <Field label="Lab charge (₹)" type="number" min="0" step="0.01" required testid="lab-case-charge-input" value={form.charge} onChange={set("charge")} />
      <Field label="Notes" textarea rows={2} testid="lab-case-notes-input" value={form.notes} onChange={set("notes")} />
      <ErrorBox error={error} testid="lab-case-error" />
      <button className="primary full" disabled={busy} data-testid="save-lab-case-button"><Check size={17} /> {busy ? "Saving…" : "Save lab case"}</button>
    </Modal>
  );
}

export function LabPaymentForm({ labs = [], defaultLab = "", onClose, onSaved }) {
  const [form, setForm] = useState({ lab_name: defaultLab, case_id: "", amount: "", date: today(), method: "Cash", from_drawer: true, notes: "" });
  const [clientRef] = useState(newRef);
  const cases = useLoad(form.lab_name ? `/lab/cases?lab_name=${encodeURIComponent(form.lab_name)}` : "", []);
  const { busy, error, run } = useSubmit();
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const save = () => run(async () => {
    const r = await api.post("/lab/payments", { ...form, amount: Number(form.amount), client_ref: clientRef });
    toast.success(r.data.from_drawer ? "Lab payment saved and taken from the cash drawer" : "Lab payment saved"); onSaved(r.data);
  });
  return (
    <Modal eyebrow="LABORATORY" title="Pay a lab" onClose={onClose} onSubmit={save} testid="lab-payment-form">
      <Field label="Lab name" required list="lab-names" testid="lab-payment-lab-input" value={form.lab_name} onChange={(v) => setForm({ ...form, lab_name: v, case_id: "" })} />
      <LabNames labs={labs} />
      <div className="form-two">
        <Field label="Amount (₹)" type="number" min="0.01" step="0.01" required testid="lab-payment-amount-input" value={form.amount} onChange={set("amount")} />
        <Field label="Date" type="date" required testid="lab-payment-date-input" value={form.date} onChange={set("date")} />
      </div>
      <Field label="Paid by" testid="lab-payment-method-select" value={form.method} onChange={set("method")} options={METHODS} />
      {form.method === "Cash" && <label className="check-row"><input type="checkbox" checked={form.from_drawer} onChange={(e) => set("from_drawer")(e.target.checked)} data-testid="lab-payment-drawer-checkbox" /> Paid from the clinic cash drawer (shows in the cashbook)</label>}
      <Field label="For case (optional)" testid="lab-payment-case-select" value={form.case_id} onChange={set("case_id")} options={[{ value: "", label: "General payment to this lab" }, ...cases.data.map((c) => ({ value: c.id, label: `${c.work_type} · ${c.patient_name} · ₹${c.charge}` }))]} />
      <Field label="Notes" testid="lab-payment-notes-input" value={form.notes} onChange={set("notes")} />
      <ErrorBox error={error} testid="lab-payment-error" />
      <button className="primary full" disabled={busy} data-testid="save-lab-payment-button"><Check size={17} /> {busy ? "Saving…" : "Save payment"}</button>
    </Modal>
  );
}
